import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import TableTitle from '../components/TableTitle';
import Pagination from '../components/Pagination/Pagination';
import usePagination from '../components/Pagination/usePagination';
import useCopyText from '../hooks/use-copy-text';
import { PAYMENT_STATUS, usePaymentsStore } from '../features/payments/state/use-payments-store';
import SearchBar from '../components/SearchBar/SearchBar';
import { Container, Table, Tooltip } from '../components';
import PersonIcon from '@mui/icons-material/Person';

const LIMIT = 20;
const INITIAL_VALUES = { status: [], user_id: '' };

const Payments = () => {
  const { data, total, isLoading, error, fetchPayments, statusOptions } = usePaymentsStore();
  const { page, limit, updatePageUrl } = usePagination({ limit: LIMIT });
  const totalPages = Math.ceil(total / limit);
  const copyText = useCopyText();
  const [searchValue, setSearchValue] = useState(INITIAL_VALUES);
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  useEffect(() => {
    fetchPayments({ page, count: limit, searchParams: searchValue, sort: sortBy });
  }, [page, limit, searchValue, sortBy]);

  const handleSearch = useCallback(
    (value, name) => {
      setSearchValue((prev) => ({ ...prev, [name]: value }));
      updatePageUrl(1);
    },
    [updatePageUrl]
  );

  const handleSort = useCallback(
    (newSortBy, newSortOrder) => {
      if (newSortOrder === 'desc') {
        setSortBy('');
        setSortOrder('');
      } else {
        setSortBy(newSortBy || '');
        setSortOrder(newSortOrder || '');
      }
      updatePageUrl(1);
    },
    [updatePageUrl]
  );

  const columns = useMemo(
    () => [
      {
        key: 'id',
        title: 'ID',
        render: (value) => <div className={'min-w-max'}>{value ?? '-'}</div>,
      },
      {
        key: 'user_id',
        sortable: true,
        title: 'ID пользователя',
        render: (value) => <div>{value ?? '-'}</div>,
      },
      {
        key: 'actions',
        sortKey: 'amount',
        sortable: true,
        title: 'Сумма',
        render: (row) => (
          <div className={'min-w-max'}>
            {row?.amount} {row?.currency}
          </div>
        ),
      },
      {
        key: 'status',
        title: 'Статус',
        render: (value) => {
          const status = PAYMENT_STATUS[value];
          return (
            <div className={'min-w-[100px] flex align-center gap-x-1'}>
              <span className={status?.className ?? 'text-gray-700'}>{status?.label ?? value ?? '-'}</span>
              <span>{status?.icon}</span>
            </div>
          );
        },
      },
      {
        key: 'confirmation_url',
        title: 'Ссылка',
        classHeader: 'w-[100px]',
        render: (value) => {
          if (!value) return <span>-</span>;

          return (
            <div className={'text-center'}>
              <Tooltip text={'Скопировать'}>
                <button
                  type="button"
                  onClick={() => copyText(value, 'Ссылка скопирована')}
                  className={'p-1 rounded text-gray-500 hover:text-orange-500 hover:bg-gray-100 transition'}>
                  <ContentCopyIcon fontSize="small" />
                </button>
              </Tooltip>
            </div>
          );
        },
      },
    ],
    [copyText]
  );

  const inputs = useMemo(
    () => [
      {
        placeholder: 'Поиск по ID пользователя...',
        type: 'simpleSearch',
        defaultValue: searchValue.user_id,
        handleSearch: (value) => handleSearch(value, 'user_id'),
        prefix: <PersonIcon fontSize={'small'} className={'text-gray-500'} />,
      },
      {
        label: 'По статусу',
        type: 'filter',
        options: statusOptions,
        selectedOptions: searchValue.status,
        setSelectedOptions: (value) => handleSearch(value, 'status'),
        prefix: <InfoOutlined fontSize={'small'} className={'text-gray-500'} />,
      },
    ],
    [handleSearch, searchValue.status, statusOptions]
  );

  return (
    <Container>
      <div className="max-w-7xl mx-auto">
        <TableTitle title={'Платежи'} />
        <SearchBar inputs={inputs} />
      </div>
      <Table columns={columns} data={data ?? []} loading={isLoading} error={error} sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>
        {isLoading || error || totalPages === 0 ? null : <Pagination totalPages={totalPages} withLimit={true} initialLimit={LIMIT} />}
      </Table>
    </Container>
  );
};
export default Payments;
